// Template schema + ready-made presets.
//
// A template is plain JSON (storable, copyable). `kind` picks the renderer:
// 'list' for now; 'invoice', 'receipt', 'cheque' … later, sharing the
// page / theme / header / footer parts.
//
// Header/footer use "slots": each element is placed at
//   'start' | 'center' | 'end' | 'hide'
// start/end follow the document direction (start = right in RTL).

export function baseListTemplate() {
  return {
    id: null,
    name: '',
    kind: 'list',
    builtIn: false,
    direction: 'auto', // auto | rtl | ltr
    page: {
      size: 'A4',
      orientation: 'landscape', // default for every print (built-ins inherit it)
      customW: 210,
      customH: 297,
      margins: { top: 10, right: 10, bottom: 10, left: 10 },
    },
    theme: {
      fontId: 'vazirmatn',
      fontSize: 9,
      text: '#1e293b',
      accent: '#1976d2',
      headerStyle: 'solid', // table head: solid | soft | none
      borders: 'horizontal', // grid | horizontal | none
      borderColor: '#d5dbe5',
      zebra: true,
      zebraColor: '#f6f8fb',
      density: 'normal', // compact | normal | comfortable
      cellAlign: 'start', // default text alignment: start | center | end
      numberAlign: 'end', // numbers/money: start | center | end
      faDigits: true,
      decimals: 0,
      radius: 0, // mm, frames of header box / table / parties / totals
    },
    header: {
      show: true,
      repeat: true,
      height: 20,
      style: 'line', // line | band | boxed | plain
      slots: { logo: 'start', company: 'start', title: 'center', meta: 'end' },
      title: '',
      subtitle: '',
      showSubtitle: true,
      showDate: true,
      showUser: true,
      showCount: true,
    },
    footer: {
      show: true,
      height: 7,
      style: 'line', // line | band | plain
      slots: { text: 'start', date: 'hide', page: 'end' },
      text: '',
      signatures: false,
      signatureLabels: [],
    },
    totals: {
      page: true, // sum of this page's rows
      cumulative: true, // running sum up to and including this page
      carry: false, // "brought forward" row at the top of each page
      grand: true, // grand total at the end
    },
    showRowNumber: true,
    // Invoice parts (rendered only when the report passes them in ctx)
    parties: { seller: false, buyer: false },
    showSummary: true,
    showNote: true,
    itemLayout: 'auto',
    options: {}, // report toggles (see PrintStudio `options` prop)
    partiesStack: false, // parties one under the other (labels)
    showTable: true, // auto (stacked on roll paper) | table | stacked
    watermark: '',
    columns: [], // [{ key, visible, width, label?, align? }]
  };
}

function clone(o) {
  return JSON.parse(JSON.stringify(o));
}

// Deep-merge plain objects (arrays replaced).
export function deepMerge(base, patch) {
  var out = clone(base);
  Object.keys(patch || {}).forEach(function (k) {
    var v = patch[k];
    if (v && typeof v === 'object' && !Array.isArray(v) && out[k] && typeof out[k] === 'object') {
      out[k] = deepMerge(out[k], v);
    } else out[k] = clone(v === undefined ? null : v);
  });
  return out;
}

// ---- Preset libraries (names are i18n keys) ----

export var HEADER_PRESETS = [
  {
    id: 'classic',
    name: 'printHdrClassic',
    patch: { style: 'line', height: 20, slots: { logo: 'start', company: 'start', title: 'center', meta: 'end' } },
  },
  {
    id: 'centered',
    name: 'printHdrCentered',
    patch: { style: 'line', height: 26, slots: { logo: 'center', company: 'center', title: 'center', meta: 'end' } },
  },
  {
    id: 'banner',
    name: 'printHdrBanner',
    patch: { style: 'band', height: 18, slots: { logo: 'start', company: 'end', title: 'start', meta: 'end' } },
  },
  {
    id: 'boxed',
    name: 'printHdrBoxed',
    patch: { style: 'boxed', height: 22, slots: { logo: 'end', company: 'start', title: 'start', meta: 'center' } },
  },
  {
    id: 'minimal',
    name: 'printHdrMinimal',
    patch: { style: 'plain', height: 13, slots: { logo: 'hide', company: 'hide', title: 'start', meta: 'end' } },
  },
];

export var FOOTER_PRESETS = [
  { id: 'simple', name: 'printFtrSimple', patch: { style: 'line', slots: { text: 'start', date: 'hide', page: 'end' } } },
  { id: 'centered', name: 'printFtrCentered', patch: { style: 'plain', slots: { text: 'hide', date: 'hide', page: 'center' } } },
  { id: 'detailed', name: 'printFtrDetailed', patch: { style: 'line', slots: { text: 'start', date: 'center', page: 'end' } } },
  { id: 'band', name: 'printFtrBand', patch: { style: 'band', slots: { text: 'start', date: 'hide', page: 'end' } } },
];

export var TABLE_PRESETS = [
  {
    id: 'modern',
    name: 'printTblModern',
    patch: { headerStyle: 'solid', borders: 'horizontal', zebra: true, borderColor: '#d5dbe5' },
  },
  {
    id: 'grid',
    name: 'printTblGrid',
    patch: { headerStyle: 'soft', borders: 'grid', zebra: false, borderColor: '#9aa5b5' },
  },
  {
    id: 'minimal',
    name: 'printTblMinimal',
    patch: { headerStyle: 'none', borders: 'horizontal', zebra: false, borderColor: '#e2e8f0' },
  },
  {
    id: 'striped',
    name: 'printTblStriped',
    patch: { headerStyle: 'soft', borders: 'none', zebra: true, zebraColor: '#eef3f9' },
  },
  {
    id: 'ledger',
    name: 'printTblLedger',
    patch: { headerStyle: 'solid', borders: 'grid', zebra: true, borderColor: '#cbd5e1', zebraColor: '#f8fafc' },
  },
];

function preset(list, id) {
  return (list.find(function (p) { return p.id === id; }) || list[0]).patch;
}

export function applyHeaderPreset(tpl, id) {
  tpl.header = deepMerge(tpl.header, preset(HEADER_PRESETS, id));
}
export function applyFooterPreset(tpl, id) {
  tpl.footer = deepMerge(tpl.footer, preset(FOOTER_PRESETS, id));
}
export function applyTablePreset(tpl, id) {
  tpl.theme = deepMerge(tpl.theme, preset(TABLE_PRESETS, id));
}

// ---- Full ready-made templates ----

export function builtInListTemplates() {
  var b = baseListTemplate();
  var make = function (id, name, parts, extra) {
    var t = deepMerge(b, extra || {});
    t.id = id;
    t.name = name;
    t.builtIn = true;
    t.header = deepMerge(t.header, preset(HEADER_PRESETS, parts[0]));
    t.footer = deepMerge(t.footer, preset(FOOTER_PRESETS, parts[1]));
    t.theme = deepMerge(t.theme, preset(TABLE_PRESETS, parts[2]));
    return t;
  };
  return [
    make('bi-modern', 'printTplModern', ['classic', 'simple', 'modern']),
    make('bi-classic', 'printTplClassic', ['centered', 'detailed', 'grid'], {
      theme: { accent: '#111827' },
      footer: { signatures: true },
    }),
    make('bi-compact', 'printTplCompact', ['minimal', 'centered', 'striped'], {
      page: { orientation: 'landscape', margins: { top: 7, right: 7, bottom: 7, left: 7 } },
      theme: { fontSize: 8, density: 'compact' },
    }),
    make('bi-official', 'printTplOfficial', ['boxed', 'detailed', 'ledger'], {
      theme: { accent: '#0f766e', density: 'comfortable' },
      footer: { signatures: true },
      totals: { carry: true },
    }),
    make('bi-banner', 'printTplBanner', ['banner', 'band', 'modern'], {
      theme: { accent: '#4338ca' },
    }),
  ];
}

// Invoice (single document) templates, in three families (the print choice
// dialog opens the first of each): with the seller box, without it (plain
// letterhead paper), and receipt printer (continuous roll, no pages).
export var INVOICE_FAMILIES = {
  seller: ['inv-seller', 'inv-classic', 'inv-modern', 'inv-modern-dark', 'inv-modern-teal', 'inv-minimal'],
  plain: ['inv-plain', 'inv-plain-grid'],
  receipt: ['inv-receipt', 'inv-receipt-bold', 'inv-receipt-minimal', 'inv-receipt-58'],
};

export function builtInInvoiceTemplates() {
  var b = baseListTemplate();
  var make = function (id, name, extra) {
    var t = deepMerge(b, {
      page: { orientation: 'portrait', margins: { top: 10, right: 10, bottom: 10, left: 10 } },
      header: {
        repeat: false, height: 26, showCount: false, showUser: false, showDate: false, showSubtitle: false,
        style: 'boxed', slots: { logo: 'start', company: 'start', title: 'center', meta: 'end' },
      },
      footer: { signatures: true, slots: { text: 'start', date: 'center', page: 'end' } },
      totals: { page: false, cumulative: false, carry: false, grand: false },
      theme: { fontSize: 9, borders: 'grid', headerStyle: 'solid', zebra: true, zebraColor: '#f6f8fb', borderColor: '#b8c2d0', accent: '#1e3a5f', density: 'normal', radius: 1.6 },
      parties: { seller: true, buyer: true },
      itemLayout: 'table',
    });
    t = deepMerge(t, extra || {});
    t.id = id;
    t.name = name;
    t.builtIn = true;
    return t;
  };
  var noSeller = { parties: { seller: false, buyer: true }, header: { slots: { logo: 'hide', company: 'hide' } } };
  var roll = function (size, fontSize, rollStyle) {
    return {
      page: { size: size, margins: { top: 3, right: 2.5, bottom: 4, left: 2.5 } },
      parties: { seller: false, buyer: true },
      header: { style: 'line', slots: { logo: 'center', company: 'center', title: 'center', meta: 'center' } },
      footer: { signatures: false, style: 'plain', slots: { text: 'center', date: 'center', page: 'hide' } },
      theme: { fontSize: fontSize, density: 'compact', borders: 'horizontal', headerStyle: 'none', zebra: false, accent: '#000000', text: '#000000', borderColor: '#9ca3af', radius: 0, rollStyle: rollStyle || 'dashed' },
      itemLayout: 'stacked',
    };
  };
  return [
    make('inv-seller', 'printTplInvoiceSeller'),
    // Close to the previous invoice print: black grid, centred title, plain boxes.
    make('inv-classic', 'printTplInvoiceClassic', {
      header: { style: 'plain', slots: { logo: 'start', company: 'start', title: 'center', meta: 'end' } },
      theme: { accent: '#000000', text: '#000000', borders: 'grid', headerStyle: 'soft', zebra: false, borderColor: '#000000', fontSize: 9.5, radius: 0 },
    }),
    make('inv-modern', 'printTplInvoiceModern', {
      header: { style: 'band', height: 22, slots: { logo: 'start', company: 'start', title: 'end', meta: 'end' } },
      theme: { accent: '#1565c0', borders: 'horizontal', headerStyle: 'soft', borderColor: '#d5dbe5' },
    }),
    make('inv-modern-dark', 'printTplInvoiceModernDark', {
      header: { style: 'band', height: 24, slots: { logo: 'start', company: 'start', title: 'center', meta: 'end' } },
      theme: { accent: '#0f172a', borders: 'horizontal', headerStyle: 'solid', zebra: true, zebraColor: '#f1f5f9', borderColor: '#cbd5e1', radius: 2 },
    }),
    make('inv-modern-teal', 'printTplInvoiceModernTeal', {
      header: { style: 'boxed', height: 24, slots: { logo: 'end', company: 'start', title: 'start', meta: 'center' } },
      theme: { accent: '#0f766e', borders: 'horizontal', headerStyle: 'soft', zebra: true, zebraColor: '#f0fdfa', borderColor: '#99d5cc', density: 'comfortable', radius: 2 },
    }),
    make('inv-minimal', 'printTplInvoiceMinimal', {
      header: { style: 'line', slots: { logo: 'start', company: 'start', title: 'end', meta: 'end' } },
      theme: { accent: '#111827', borders: 'horizontal', headerStyle: 'none', zebra: false, borderColor: '#e2e8f0' },
    }),
    make('inv-plain', 'printTplInvoicePlain', deepMerge(noSeller, {
      header: { style: 'line', slots: { title: 'start', meta: 'end' } },
      theme: { accent: '#1e293b', headerStyle: 'soft' },
    })),
    make('inv-plain-grid', 'printTplInvoicePlainGrid', deepMerge(noSeller, {
      page: { margins: { top: 40 } }, // room for pre-printed letterhead
      header: { style: 'plain', slots: { title: 'center', meta: 'end' } },
      theme: { accent: '#000000', headerStyle: 'soft', zebra: false, borderColor: '#6b7280' },
    })),
    make('inv-receipt', 'printTplInvoiceReceipt', roll('Roll80', 8.5)),
    make('inv-receipt-bold', 'printTplInvoiceReceiptBold', roll('Roll80', 8.5, 'bold')),
    make('inv-receipt-minimal', 'printTplInvoiceReceiptMinimal', deepMerge(roll('Roll80', 8.5, 'minimal'), {
      header: { slots: { logo: 'hide' } },
      showRowNumber: false,
    })),
    make('inv-receipt-58', 'printTplInvoiceReceipt58', roll('Roll58', 7.5)),
  ];
}

// Treasury receipts (cheque / cash, paid / received): one receipt per item,
// payer + receiver boxes, amount in words, signatures.
export function builtInReceiptTemplates() {
  var b = baseListTemplate();
  var make = function (id, name, extra) {
    var t = deepMerge(b, {
      page: { size: 'A5', orientation: 'landscape', margins: { top: 8, right: 9, bottom: 8, left: 9 } },
      header: {
        repeat: false, height: 20, showCount: false, showUser: false, showDate: false, showSubtitle: false,
        style: 'boxed', slots: { logo: 'start', company: 'start', title: 'center', meta: 'end' },
      },
      footer: { show: false, signatures: true },
      totals: { page: false, cumulative: false, carry: false, grand: false },
      theme: { fontSize: 9, borders: 'grid', headerStyle: 'soft', zebra: false, borderColor: '#b8c2d0', accent: '#1e3a5f', radius: 1.6 },
      parties: { seller: true, buyer: true },
      itemLayout: 'table',
    });
    t = deepMerge(t, extra || {});
    t.id = id;
    t.name = name;
    t.builtIn = true;
    return t;
  };
  return [
    make('rcp-a5', 'printTplReceiptA5'),
    make('rcp-a5-modern', 'printTplReceiptModern', {
      header: { style: 'band', slots: { logo: 'start', company: 'start', title: 'end', meta: 'end' } },
      theme: { accent: '#0f766e', borders: 'horizontal', headerStyle: 'solid', borderColor: '#99d5cc', radius: 2 },
    }),
    make('rcp-a4', 'printTplReceiptA4', {
      page: { size: 'A4', orientation: 'portrait', margins: { top: 10, right: 10, bottom: 10, left: 10 } },
      header: { height: 24 },
      footer: { show: true, signatures: true, slots: { text: 'start', date: 'center', page: 'hide' } },
    }),
    make('rcp-classic', 'printTplReceiptClassic', {
      header: { style: 'plain' },
      theme: { accent: '#000000', text: '#000000', borderColor: '#000000', radius: 0 },
    }),
    make('rcp-roll', 'printTplReceiptRoll', {
      page: { size: 'Roll80', margins: { top: 3, right: 2.5, bottom: 4, left: 2.5 } },
      header: { style: 'line', slots: { logo: 'center', company: 'center', title: 'center', meta: 'center' } },
      footer: { show: true, signatures: false, style: 'plain', slots: { text: 'center', date: 'center', page: 'hide' } },
      theme: { fontSize: 8.5, density: 'compact', borders: 'horizontal', headerStyle: 'none', accent: '#000000', text: '#000000', borderColor: '#9ca3af', radius: 0, rollStyle: 'dashed' },
      itemLayout: 'stacked',
      showRowNumber: false,
    }),
  ];
}

// Warehouse documents (receipt / remittance / transfer …): items table with
// counts, both sides, deliverer / recipient signatures.
export function builtInWarehouseTemplates() {
  var b = baseListTemplate();
  var make = function (id, name, extra) {
    var t = deepMerge(b, {
      page: { size: 'A4', orientation: 'portrait', margins: { top: 10, right: 10, bottom: 10, left: 10 } },
      header: {
        repeat: true, height: 22, showCount: false, showUser: false, showDate: false, showSubtitle: false,
        style: 'boxed', slots: { logo: 'start', company: 'start', title: 'center', meta: 'end' },
      },
      footer: { signatures: true, slots: { text: 'start', date: 'center', page: 'end' } },
      totals: { page: false, cumulative: false, carry: false, grand: true },
      theme: { fontSize: 9, borders: 'grid', headerStyle: 'solid', zebra: true, borderColor: '#b8c2d0', accent: '#334155', radius: 1.6 },
      parties: { seller: true, buyer: true },
      itemLayout: 'table',
    });
    t = deepMerge(t, extra || {});
    t.id = id;
    t.name = name;
    t.builtIn = true;
    return t;
  };
  return [
    make('wh-a4', 'printTplWarehouseA4'),
    make('wh-a5', 'printTplWarehouseA5', {
      page: { size: 'A5', orientation: 'landscape', margins: { top: 8, right: 8, bottom: 8, left: 8 } },
      header: { height: 18 },
      theme: { fontSize: 8.5, density: 'compact' },
    }),
    make('wh-classic', 'printTplWarehouseClassic', {
      header: { style: 'plain' },
      theme: { accent: '#000000', text: '#000000', borderColor: '#000000', headerStyle: 'soft', zebra: false, radius: 0 },
    }),
    make('wh-roll', 'printTplWarehouseRoll', {
      page: { size: 'Roll80', margins: { top: 3, right: 2.5, bottom: 4, left: 2.5 } },
      header: { style: 'line', slots: { logo: 'center', company: 'center', title: 'center', meta: 'center' } },
      footer: { signatures: false, style: 'plain', slots: { text: 'center', date: 'center', page: 'hide' } },
      theme: { fontSize: 8.5, density: 'compact', borders: 'horizontal', headerStyle: 'none', accent: '#000000', text: '#000000', borderColor: '#9ca3af', radius: 0, rollStyle: 'dashed' },
      itemLayout: 'stacked',
    }),
  ];
}

// Shipping label: sender + receiver (name, phone, address), no item table.
export function builtInLabelTemplates() {
  var b = baseListTemplate();
  var make = function (id, name, extra) {
    var t = deepMerge(b, {
      page: { size: 'Label100x150', orientation: 'portrait', margins: { top: 4, right: 4, bottom: 4, left: 4 } },
      header: {
        repeat: false, height: 16, showCount: false, showUser: false, showDate: false, showSubtitle: false,
        style: 'line', slots: { logo: 'start', company: 'start', title: 'hide', meta: 'end' },
      },
      footer: { show: false, signatures: false },
      totals: { page: false, cumulative: false, carry: false, grand: false },
      theme: { fontSize: 10, borders: 'none', headerStyle: 'none', zebra: false, borderColor: '#111827', accent: '#000000', text: '#000000', radius: 1.6 },
      parties: { seller: true, buyer: true },
      partiesStack: true,
      showTable: false,
      showSummary: false,
    });
    t = deepMerge(t, extra || {});
    t.id = id;
    t.name = name;
    t.builtIn = true;
    return t;
  };
  return [
    make('lbl-100x150', 'printTplLabel100x150'),
    make('lbl-a6', 'printTplLabelA6', { page: { size: 'A6' } }),
    make('lbl-a5', 'printTplLabelA5', {
      page: { size: 'A5', orientation: 'landscape', margins: { top: 8, right: 8, bottom: 8, left: 8 } },
      header: { slots: { title: 'center' } },
      theme: { fontSize: 11 },
    }),
    make('lbl-roll', 'printTplLabelRoll', {
      page: { size: 'Roll80', margins: { top: 3, right: 2.5, bottom: 4, left: 2.5 } },
      header: { slots: { logo: 'center', company: 'center', meta: 'center' } },
      theme: { fontSize: 9, radius: 0, rollStyle: 'dashed' },
    }),
  ];
}

// Bring any stored template (incl. older shapes) up to the current schema.
export function normalize(tpl) {
  var t = deepMerge(baseListTemplate(), tpl || {});
  var h = (tpl && tpl.header) || {};
  if (h.showLogo === false && !(h.slots && h.slots.logo)) t.header.slots.logo = 'hide';
  if (h.showCompany === false && !(h.slots && h.slots.company)) t.header.slots.company = 'hide';
  var f = (tpl && tpl.footer) || {};
  if (f.pageNumbers === false && !(f.slots && f.slots.page)) t.footer.slots.page = 'hide';
  if (tpl && tpl.theme && tpl.theme.font && !tpl.theme.fontId) t.theme.fontId = 'vazirmatn';
  return t;
}

// Fill template columns from the report's column definitions (keeps saved
// order, appends new report columns, drops ones that no longer exist).
export function syncColumns(tpl, defs) {
  var have = {};
  var cols = (tpl.columns || []).filter(function (c) {
    var ok = defs.some(function (d) { return d.key === c.key; });
    if (ok) have[c.key] = true;
    return ok;
  });
  defs.forEach(function (d) {
    if (!have[d.key]) cols.push({ key: d.key, visible: d.defaultVisible !== false, width: d.width || 1, align: 'auto' });
  });
  cols.forEach(function (c) {
    if (!c.align) c.align = 'auto';
  });
  tpl.columns = cols;
  return tpl;
}

export function newId() {
  return 'tpl-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}
